/**
 * Form model for the RECODE wizard. A RECODE has one target question and one
 * of three right-hand-side shapes:
 *  - bucket      — per answer option, a boolean premise (autocoding)
 *  - select_n    — SELECT_N(source, N, {priority}) for a multi-select target
 *  - arithmetic  — a numeric expression for a FILL IN NUMBER/REAL target
 */
import { ASTNode } from "../logicWizard/grammar"
import { isASTComplete } from "../logicWizard/ifGenerator"
import { QuestionInfo } from "../logicWizard/questionTypeDetector"
import { QuestionType } from "../logicWizard/grammar"
import { parseArith } from "./arithParser"

export type RecodeMode = "bucket" | "select_n" | "arithmetic"

/** One `target::<<option>> = <premise>` assignment. */
export interface BucketAssignment {
  option: string
  ast: ASTNode
}

export interface SelectNConfig {
  /** Source question label (must be multi-select). */
  source: string
  /** Number of items to keep. Must be a literal positive integer. */
  n: number
  /** Priority options in order; empty = pure random. */
  priority: string[]
}

export interface RecodeForm {
  /** Target question label. */
  target: string
  mode: RecodeMode
  buckets: BucketAssignment[]
  selectN: SelectNConfig
  arithmeticText: string
}

export function makeEmptyRecodeForm(): RecodeForm {
  return {
    target: "",
    mode: "bucket",
    buckets: [],
    selectN: { source: "", n: 1, priority: [] },
    arithmeticText: "",
  }
}

/** Underlying FILL IN kind, accounting for the HIDDEN collapse. */
export function fillKindOf(info: QuestionInfo): QuestionType | undefined {
  if (
    info.type === QuestionType.FILL_IN_NUMBER ||
    info.type === QuestionType.FILL_IN_REAL ||
    info.type === QuestionType.FILL_IN_TEXT
  ) {
    return info.type
  }
  return info.fillKind
}

/** Underlying SELECT cardinality, accounting for the HIDDEN/GRID collapse. */
export function selectKindOf(info: QuestionInfo): "ONE" | "MULTIPLE" | undefined {
  if (info.type === QuestionType.SELECT_ONE) return "ONE"
  if (info.type === QuestionType.SELECT_MULTIPLE) return "MULTIPLE"
  return info.selectKind
}

export function isMultiSelect(info: QuestionInfo | undefined): boolean {
  return !!info && selectKindOf(info) === "MULTIPLE"
}

/**
 * Which recode modes a target of the given type supports:
 *  - FILL IN NUMBER/REAL → arithmetic
 *  - FILL IN TEXT        → none (backend doesn't support it)
 *  - SELECT MULTIPLE     → bucket + select_n
 *  - SELECT ONE          → bucket
 *  - unknown             → all three (let the backend validate)
 */
export function allowedModesFor(info: QuestionInfo | undefined): RecodeMode[] {
  if (!info) return ["bucket", "select_n", "arithmetic"]

  const fk = fillKindOf(info)
  if (fk === QuestionType.FILL_IN_NUMBER || fk === QuestionType.FILL_IN_REAL) {
    return ["arithmetic"]
  }
  if (fk === QuestionType.FILL_IN_TEXT) return []

  const sk = selectKindOf(info)
  if (sk === "MULTIPLE") return ["bucket", "select_n"]
  if (sk === "ONE") return ["bucket"]

  return ["bucket", "select_n", "arithmetic"]
}

/** True when the form has enough filled in to emit valid RECODE text. */
export function isRecodeComplete(form: RecodeForm): boolean {
  if (!form.target.trim()) return false
  switch (form.mode) {
    case "bucket":
      return (
        form.buckets.length > 0 &&
        form.buckets.every(
          (b) => b.option.trim() !== "" && isASTComplete(b.ast),
        )
      )
    case "select_n":
      return (
        form.selectN.source.trim() !== "" &&
        Number.isInteger(form.selectN.n) &&
        form.selectN.n >= 1
      )
    case "arithmetic":
      return (
        form.arithmeticText.trim() !== "" &&
        parseArith(form.arithmeticText).errors.length === 0
      )
  }
}
